import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase/supabase';
import { useLeague } from './LeagueContext';
import { useAuth } from './AuthContext';

const DraftContext = createContext(null);

export function DraftProvider({ children }) {
  const { leagueId } = useParams();
  const { members, isAdmin, isCoAdmin } = useLeague();
  const { currentUser, teams: authTeams } = useAuth();
  const currentUserId = currentUser?.id ?? null;

  const [draft, setDraft] = useState(null);
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refetch of authoritative draft state. Realtime is the mechanism for
  // seeing *other* people's picks, but the person who just made a pick should
  // never be waiting on a websocket round-trip to see their own — and if the
  // tables are missing from the realtime publication, nothing arrives at all.
  // So every mutation calls this directly.
  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!leagueId) return;
    if (!quiet) setLoading(true);
    try {
      const [
        { data: draftRow, error: draftErr },
        { data: pickRows,  error: picksErr  },
      ] = await Promise.all([
        supabase.from('drafts').select('*').eq('league_id', leagueId).single(),
        supabase.from('draft_picks').select('*').eq('league_id', leagueId).order('pick_number'),
      ]);

      if (draftErr) throw draftErr;
      if (picksErr) throw picksErr;

      setDraft(draftRow);
      setPicks(pickRows ?? []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (!leagueId) return;

    refresh();

    const channel = supabase
      .channel(`draft-ctx:${leagueId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'drafts', filter: `league_id=eq.${leagueId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE') setDraft((prev) => ({ ...prev, ...payload.new }));
        }
      )
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'draft_picks', filter: `league_id=eq.${leagueId}` },
        (payload) => {
          setPicks((prev) => {
            if (prev.some((p) => p.id === payload.new.id)) return prev;
            return [...prev, payload.new].sort((a, b) => a.pick_number - b.pick_number);
          });
        }
      )
      // edit_pick rewrites a row in place, so every viewer needs UPDATE too —
      // otherwise a corrected pick only appears for whoever made the edit.
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'draft_picks', filter: `league_id=eq.${leagueId}` },
        (payload) => {
          setPicks((prev) => prev.map((p) => (p.id === payload.new.id ? payload.new : p)));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [leagueId, refresh]);

  // Convert AuthContext teams map (keyed by normalized name) to FBS array
  const teams = useMemo(() =>
    Object.values(authTeams).filter((t) => (t.classification || '').toLowerCase() === 'fbs'),
  [authTeams]);

  const pickedTeamIds = useMemo(() => new Set(picks.map((p) => p.team_id)), [picks]);
  const availableTeams = useMemo(() => teams.filter((t) => !pickedTeamIds.has(t.id)), [teams, pickedTeamIds]);

  const nManagers      = draft?.draft_order?.length ?? 0;
  const totalRounds    = draft?.total_rounds ?? 9;
  const totalPicks     = nManagers * totalRounds;
  const currentPickIndex  = draft?.current_pick ?? 0;
  const currentPickerUid  = draft?.status === 'active'
    ? snakePicker(draft.draft_order, currentPickIndex)
    : null;
  const isMyTurn   = !!currentPickerUid && currentPickerUid === currentUserId;

  // The manager on the clock picks for themselves. The admin and up to two
  // co-admins can enter that pick instead, at any time, which is both the
  // failsafe for an unreachable manager and how a league that drafts entirely
  // outside the app gets its picks in. There is no per-league draft mode:
  // make_pick applies the same rule to every draft.
  const canEnterPicks = isAdmin || isCoAdmin;
  const canPickNow    = isMyTurn || canEnterPicks;

  // Keyed by user_id — consistent with draft_order. draft_picks stores
  // member_id (league_members.id), so map it back through members first.
  const userIdByMemberId = useMemo(() => members.reduce((acc, m) => {
    acc[m.id] = m.user_id;
    return acc;
  }, {}), [members]);

  const rosterByUser = useMemo(() => picks.reduce((acc, p) => {
    const uid = userIdByMemberId[p.member_id];
    if (!uid) return acc;
    if (!acc[uid]) acc[uid] = [];
    acc[uid].push(p.team_id);
    return acc;
  }, {}), [picks, userIdByMemberId]);

  // Keyed by user_id (was incorrectly keyed by league_members.id before)
  const memberMap = useMemo(() => members.reduce((acc, m) => {
    acc[m.user_id] = {
      teamName:  m.team_name,
      firstName: m.first_name || '',
      lastName:  m.last_name  || '',
      name:      m.first_name ? `${m.first_name} ${m.last_name ?? ''}`.trim() : m.team_name,
      memberId:  m.id,
    };
    return acc;
  }, {}), [members]);

  // Same shape as memberMap but keyed by league_members.id, for anything
  // reading draft_picks — those rows carry member_id, not user_id.
  const memberByMemberId = useMemo(() => members.reduce((acc, m) => {
    acc[m.id] = {
      teamName:  m.team_name,
      firstName: m.first_name || '',
      lastName:  m.last_name  || '',
      name:      m.first_name ? `${m.first_name} ${m.last_name ?? ''}`.trim() : m.team_name,
      userId:    m.user_id,
    };
    return acc;
  }, {}), [members]);

  const makePick = useCallback(async (teamId) => {
    if (!draft?.id) return { error: 'No draft' };
    const { data, error } = await supabase.rpc('make_pick', {
      p_draft_id: draft.id,
      p_team_id:  teamId,
    });
    if (error) return { error: error.message };
    if (!data?.error) await refresh({ quiet: true });
    return data;
  }, [draft?.id, refresh]);

  const editPick = useCallback(async (pickId, teamId) => {
    const { data, error } = await supabase.rpc('edit_pick', {
      p_pick_id: pickId,
      p_team_id: teamId,
    });
    if (error) return { error: error.message };
    if (!data?.error) await refresh({ quiet: true });
    return data;
  }, [refresh]);

  const startDraft = useCallback(async () => {
    const { data, error } = await supabase.rpc('start_draft', { p_league_id: leagueId });
    if (error) return { error: error.message };
    if (data?.success) {
      const { data: updated } = await supabase.from('drafts').select('*').eq('league_id', leagueId).single();
      if (updated) setDraft(updated);
    }
    return data;
  }, [leagueId]);

  const saveDraftOrder = useCallback(async (order) => {
    if (!draft?.id) return { error: 'No draft' };
    const { error } = await supabase.from('drafts').update({ draft_order: order }).eq('id', draft.id);
    if (error) return { error: error.message };
    return { success: true };
  }, [draft?.id]);

  return (
    <DraftContext.Provider value={{
      draft, picks, teams, pickedTeamIds, availableTeams,
      currentPickerUid, currentPickIndex, isMyTurn,
      canEnterPicks, canPickNow,
      totalPicks, totalRounds, nManagers,
      rosterByUser, memberMap, memberByMemberId, currentUserId,
      loading, error,
      makePick, editPick, startDraft, saveDraftOrder, refresh,
    }}>
      {children}
    </DraftContext.Provider>
  );
}

export function useDraftContext() {
  return useContext(DraftContext);
}

function snakePicker(order, pickIndex) {
  if (!order || order.length === 0) return null;
  const n     = order.length;
  const round = Math.floor(pickIndex / n);
  const pos   = round % 2 === 0 ? pickIndex % n : (n - 1) - (pickIndex % n);
  return order[pos];
}
